// ═══════════════════════════════════════════════════════════════════
//  Los colores de los estados, cargados UNA vez al entrar (entrega 278)
//
//  Qué hace:
//    1. En cuanto hay usuario, pide `GET /api/estados-color`.
//    2. Con la respuesta pisa las tablas ESTADO_COLOR y PRESUPUESTO_COLOR de
//       `utils/estadoServicio.js`, que es de donde pintan todas las pantallas.
//    3. Avisa de que ya están (`listo`), para que el menú monte la pantalla.
//
//  Si el servidor no contesta (red caída, SQL de la 278 sin pasar), se
//  queda con los colores de siempre y `listo` se pone igualmente: la
//  aplicación tiene que seguir funcionando aunque no se puedan cambiar
//  los colores. Un color viejo no es motivo para no trabajar.
//
//  `recargar()` lo usa la pantalla de Colores después de guardar: vuelve a
//  pedir la tabla, la aplica y sube `version`, con lo que el menú y todo lo
//  que cuelga de él se repintan con los colores nuevos sin salir y entrar.
// ═══════════════════════════════════════════════════════════════════
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { aplicarColoresEstado } from '../utils/estadoServicio';

const ColoresEstadoContext = createContext(null);

export function ColoresEstadoProvider({ children }) {
  const { user } = useAuth();
  const [listo,   setListo]   = useState(false);
  const [filas,   setFilas]   = useState([]);
  const [version, setVersion] = useState(0);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/estados-color');
      const lista = Array.isArray(data && data.data) ? data.data : [];
      aplicarColoresEstado(lista);
      setFilas(lista);
    } catch {
      // Sin tabla o sin red: colores de siempre. No se avisa con globo
      // porque pasaría en cada entrada mientras no esté el SQL, y el
      // cartel de versiones ya cuenta que la instalación va a medias.
      setFilas([]);
    } finally {
      setListo(true);
      setVersion(v => v + 1);
    }
  }, []);

  // Al entrar (o al cambiar de usuario) se cargan. Sin usuario no se piden:
  // la ruta exige sesión y daría 401 en la pantalla de entrada.
  useEffect(() => {
    if (!user) { setListo(false); return; }
    cargar();
  }, [user, cargar]);

  const recargar = useCallback(() => cargar(), [cargar]);

  return (
    <ColoresEstadoContext.Provider value={{ listo, filas, version, recargar }}>
      {children}
    </ColoresEstadoContext.Provider>
  );
}

// Si alguien monta una pantalla fuera del proveedor (una prueba suelta, por
// ejemplo), que no se caiga: se da por cargado con los colores de siempre.
const SIN_PROVEEDOR = { listo: true, filas: [], version: 0, recargar: () => Promise.resolve() };

export function useColoresEstado() {
  return useContext(ColoresEstadoContext) || SIN_PROVEEDOR;
}
